import { TileFragmentFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { AreaVars } from '@/integrations/crystallize/types/localization';
import { getContentTileByPath } from './content-tiles-actions';

/**
 * A reference to a tile linked from an "Installation check - Results" content field. Only the
 * fields needed to resolve the tile's language-specific path are kept; the full tile is then
 * fetched separately with the rich tile fragment so it renders like on a regular content page.
 */
export interface ContentTileRef {
  id: string;
  path?: string | null;
  paths?: {
    hreflangs?: Array<{ path?: string | null; language?: string | null }> | null;
  } | null;
}

/**
 * Reads the item-relation tiles configured on an "Installation check - Results" content field
 * (e.g. `content-tiles-top` / `content-tiles-bottom`). Items without an id or path are skipped
 * since a path is required to fetch the full tile.
 */
export function extractContentTileItems(
  catalogueComponents: any[] | undefined,
  fieldId: string,
): ContentTileRef[] {
  if (!catalogueComponents) {
    return [];
  }

  const field = catalogueComponents.find((component: any) => component.id === fieldId);
  const items = field?.content?.items;

  if (!items) {
    return [];
  }

  return items.filter((item: any) => Boolean(item?.id) && Boolean(item?.path));
}

// Fetches each content-field tile (an item relation) as a full tile document via the shared Tile
// query, so CMS tiles placed on the result page render with the same fidelity as on a regular page.
export const fetchContentTiles = async (
  language: string,
  areaVariables: AreaVars | undefined,
  tilePaths: string[],
): Promise<TileFragmentFragment[]> => {
  if (!areaVariables || tilePaths.length === 0) {
    return [];
  }

  const fetched = await Promise.all(
    tilePaths.map((path) =>
      getContentTileByPath(
        path,
        language,
        areaVariables.priceVariantId,
        areaVariables.stockLocationId,
      ),
    ),
  );

  return fetched.filter(
    (tile): tile is { __typename: 'Document' } & TileFragmentFragment =>
      tile?.__typename === 'Document',
  );
};
